import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ElementRef, ViewChild, AfterViewInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-titan-dialog',
  standalone: true,
  templateUrl: './titan-dialog.component.html',
  styleUrls: ['./titan-dialog.component.scss'],
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TitanDialogComponent implements AfterViewInit, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() ariaLabel?: string;
  @Input() isDismissable: boolean = true;
  @Input() trapFocus: boolean = true;
  
  @Output() onClose = new EventEmitter<void>();
  @Output() onOpen = new EventEmitter<void>();

  @ViewChild('dialogElement', { static: false }) dialogElement?: ElementRef<HTMLElement>;

  private previousActiveElement?: HTMLElement;
  private focusableElements: HTMLElement[] = [];

  ngAfterViewInit() {
    if (this.isOpen) {
      this.setupFocusManagement();
    }
  }

  ngOnDestroy() {
    this.restoreFocus();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (!this.isOpen) return;

    if (this.isDismissable && event.key === 'Escape') {
      this.closeDialog();
      return;
    }

    if (this.trapFocus && event.key === 'Tab') {
      this.handleTabKey(event);
    }
  }

  handleOverlayClick(event: Event) {
    if (this.isDismissable && event.target === event.currentTarget) {
      this.closeDialog();
    }
  }

  private setupFocusManagement() {
    if (!this.trapFocus) return;

    // Store the previously focused element
    this.previousActiveElement = document.activeElement as HTMLElement;
    
    // Get all focusable elements within the dialog
    this.updateFocusableElements();
    
    // Focus the first focusable element or the dialog itself
    const firstFocusable = this.focusableElements[0] || this.dialogElement?.nativeElement;
    if (firstFocusable) {
      firstFocusable.focus();
    }

    // Emit open event
    this.onOpen.emit();
  }

  private updateFocusableElements() {
    if (!this.dialogElement?.nativeElement) return;

    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    this.focusableElements = Array.from(
      this.dialogElement.nativeElement.querySelectorAll(focusableSelectors)
    ) as HTMLElement[];
  }

  private handleTabKey(event: KeyboardEvent) {
    if (this.focusableElements.length === 0) return;

    const firstElement = this.focusableElements[0];
    const lastElement = this.focusableElements[this.focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement;

    if (event.shiftKey) {
      // Shift + Tab: move backwards
      if (activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab: move forwards
      if (activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  private restoreFocus() {
    if (this.previousActiveElement && this.previousActiveElement.focus) {
      this.previousActiveElement.focus();
    }
  }

  private closeDialog() {
    this.restoreFocus();
    this.onClose.emit();
  }
}
